import type { BoardObjectV2, SceneV2, SegmentBindings } from "@bezpart-board/shared";
import type { FlatBoardOp } from "./opsTypes.js";
import { mergeBindingsPatch } from "../bindings/resolveBindings.js";

const LEGACY_STYLE_KEYS = [
  "stroke",
  "strokeWidth",
  "strokeOpacity",
  "strokeLinecap",
  "strokeLinejoin",
  "strokeDash",
  "fill",
  "color",
] as const;

export function cloneScene(scene: SceneV2): SceneV2 {
  return JSON.parse(JSON.stringify(scene)) as SceneV2;
}

/**
 * Копия для применения ops: новый массив, объекты — те же ссылки.
 *
 * Хватает, потому что ниже ни одна ветка не мутирует существующий объект:
 * update кладёт на его место результат mergeObjectPatch (всегда новый объект),
 * add/delete меняют только сам массив. Глубокий клон здесь был самой дорогой
 * операцией редактора — он выполнялся на каждый штрих и каждый сдвиг,
 * а на большой доске это десятки миллисекунд на ровном месте.
 */
function shallowCloneScene(scene: SceneV2): SceneV2 {
  return { version: scene.version ?? 2, objects: scene.objects.slice() };
}

function mergeStylePatch(
  obj: BoardObjectV2,
  patch: Record<string, unknown>
): BoardObjectV2["style"] {
  const stylePatch =
    patch.style && typeof patch.style === "object"
      ? (patch.style as Record<string, unknown>)
      : {};

  const legacy: Record<string, unknown> = {};
  for (const key of LEGACY_STYLE_KEYS) {
    if (patch[key] !== undefined) {
      if (key === "color") {
        legacy.backgroundColor = patch.color;
      } else {
        legacy[key] = patch[key];
      }
    }
  }

  const merged = { ...(obj.style ?? {}), ...legacy, ...stylePatch };
  return Object.keys(merged).length ? merged : obj.style;
}

function mergeObjectPatch(obj: BoardObjectV2, patch: Record<string, unknown>): BoardObjectV2 {
  const {
    style: _s,
    bindings: bindingsPatch,
    endsAt: endsAtPatch,
    frameId: frameIdPatch,
    meta: metaPatch,
    color: _color,
    fill: _fill,
    stroke: _stroke,
    strokeWidth: _strokeWidth,
    strokeOpacity: _strokeOpacity,
    strokeLinecap: _strokeLinecap,
    strokeLinejoin: _strokeLinejoin,
    strokeDash: _strokeDash,
    ...restPatch
  } = patch;
  const next = { ...obj, ...restPatch } as BoardObjectV2;
  for (const key of LEGACY_STYLE_KEYS) {
    delete (next as Record<string, unknown>)[key];
  }
  // points приходит в патче ссылкой на живой массив объекта, а drag/resize
  // правят точки на месте. Без своей копии сцена и undo-запись делили бы
  // один массив, и откат штриха после переноса возвращал бы новые координаты.
  const points = (restPatch as { points?: unknown }).points;
  if (Array.isArray(points)) {
    // Точка пути — это КОРТЕЖ [x, y], а не объект. Копировать её через
    // spread нельзя: {...[1,2]} даёт {0:1,1:2}, и перебор точек падает
    // с "not iterable", ломая отрисовку всех штрихов на доске.
    (next as Record<string, unknown>).points = points.map((pt) =>
      Array.isArray(pt) ? pt.slice() : pt
    );
  }
  if (endsAtPatch === null) {
    delete (next as Record<string, unknown>).endsAt;
  } else if (typeof endsAtPatch === "number") {
    (next as Record<string, unknown>).endsAt = endsAtPatch;
  }
  if (frameIdPatch === null) {
    delete (next as Record<string, unknown>).frameId;
  } else if (typeof frameIdPatch === "string") {
    (next as Record<string, unknown>).frameId = frameIdPatch;
  }
  if (metaPatch !== undefined) {
    if (
      metaPatch === null ||
      (typeof metaPatch === "object" &&
        metaPatch !== null &&
        Object.keys(metaPatch as object).length === 0)
    ) {
      delete (next as Record<string, unknown>).meta;
    } else if (typeof metaPatch === "object" && metaPatch !== null) {
      next.meta = { ...(metaPatch as Record<string, unknown>) };
    }
  }
  const mergedStyle = mergeStylePatch(obj, patch);
  if (mergedStyle !== undefined) {
    next.style = mergedStyle;
  }
  if (bindingsPatch !== undefined && (obj.type === "line" || obj.type === "arrow")) {
    const merged = mergeBindingsPatch(
      (obj as { bindings?: SegmentBindings }).bindings,
      bindingsPatch as SegmentBindings | null
    );
    if (merged) {
      (next as { bindings?: SegmentBindings }).bindings = merged;
    } else {
      delete (next as Record<string, unknown>).bindings;
    }
  }
  return next;
}

/** Локальное применение ops (optimistic / undo). Без полной Zod-валидации. */
export function applyLocalOps(scene: SceneV2, ops: FlatBoardOp[]): SceneV2 | null {
  if (!scene || !Array.isArray(scene.objects)) return null;
  const next = shallowCloneScene(scene);

  for (const op of ops) {
    if (op.type === "add_object") {
      const id = String(op.object.id ?? "");
      if (!id || next.objects.some((o) => o.id === id)) return null;
      next.objects.push(op.object as BoardObjectV2);
      continue;
    }

    if (op.type === "update_object") {
      const idx = next.objects.findIndex((o) => o.id === op.id);
      if (idx < 0) return null;
      next.objects[idx] = mergeObjectPatch(next.objects[idx]!, op.patch);
      continue;
    }

    if (op.type === "delete_object") {
      const idx = next.objects.findIndex((o) => o.id === op.id);
      if (idx < 0) return null;
      next.objects.splice(idx, 1);
    }
  }

  return next;
}

/**
 * То же, но неприменимые ops пропускаются вместо отказа от всего пакета.
 *
 * Нужно, когда серверный snapshot приходит поверх наших ещё не подтверждённых
 * правок: часть из них сервер уже применил (объект в снапшоте есть), часть — нет.
 * Строгий applyLocalOps вернул бы null, и правка исчезла бы до следующего ack —
 * это и видно как «линия сначала пропала, потом появилась».
 */
export function applyLocalOpsLenient(
  scene: SceneV2,
  ops: FlatBoardOp[]
): { scene: SceneV2; applied: number; skipped: number } {
  const next = shallowCloneScene(scene);
  let applied = 0;
  let skipped = 0;

  for (const op of ops) {
    if (op.type === "add_object") {
      const id = String(op.object.id ?? "");
      if (!id || next.objects.some((o) => o.id === id)) {
        skipped++;
        continue;
      }
      next.objects.push(op.object as BoardObjectV2);
      applied++;
      continue;
    }

    const idx = next.objects.findIndex((o) => o.id === op.id);
    if (idx < 0) {
      skipped++;
      continue;
    }

    if (op.type === "update_object") {
      next.objects[idx] = mergeObjectPatch(next.objects[idx]!, op.patch);
    } else {
      next.objects.splice(idx, 1);
    }
    applied++;
  }

  return { scene: next, applied, skipped };
}
